import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { COMPANY, TAGLINE, PHONE_DISPLAY, PHONE_TEL, EMAIL, ADDRESS, NAV_LINKS, waLink, track } from "@/lib/site";
import { useSettings } from "@/hooks/useContent";

const PRODUCT_LINKS = [
  { to: "/products?category=rice", label: "Rice" },
  { to: "/products?category=spices", label: "Spices" },
  { to: "/products?category=makhana", label: "Makhana" },
  { to: "/products?category=dehydrated-fruits-vegetables", label: "Dehydrated Fruits & Vegetables" },
  { to: "/products?category=peanuts", label: "Peanuts" },
  { to: "/products?category=tea", label: "Tea" },
  { to: "/products?category=food-powders", label: "Food Powders" },
];

export default function Footer() {
  const { data: settings } = useSettings();
  const socials = Object.entries(settings?.social || {}).filter(([, v]) => v);

  return (
    <footer className="border-t border-line bg-sand text-navy" data-testid="site-footer">
      <div className="container-x grid gap-10 py-14 md:grid-cols-2 lg:grid-cols-4">
        <div>
          <h3 className="font-serif text-lg font-semibold leading-snug">{COMPANY}</h3>
          <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-gold-dark">{TAGLINE}</p>
          <p className="mt-4 text-sm leading-relaxed text-mist">
            India-based B2B supplier and exporter of food and agricultural products serving international sourcing requirements.
          </p>
          {socials.length > 0 && (
            <div className="mt-4 flex gap-3">
              {socials.map(([name, url]) => (
                <a key={name} href={url} target="_blank" rel="noopener noreferrer" className="text-xs font-bold uppercase tracking-wider text-navy underline decoration-gold decoration-2 underline-offset-4">
                  {name}
                </a>
              ))}
            </div>
          )}
        </div>

        <nav aria-label="Company links">
          <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-mist">Company</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="transition-colors hover:text-gold-dark">{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav aria-label="Product links">
          <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-mist">Products</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            {PRODUCT_LINKS.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="transition-colors hover:text-gold-dark">{l.label}</Link>
              </li>
            ))}
          </ul>
        </nav>

        <div>
          <h4 className="text-xs font-bold uppercase tracking-[0.18em] text-mist">Contact</h4>
          <ul className="mt-4 space-y-3 text-sm">
            <li>
              <a href={PHONE_TEL} data-testid="footer-phone-link" onClick={() => track("phone_click")} className="inline-flex items-center gap-2.5 hover:text-gold-dark">
                <Phone size={15} className="text-gold-dark" /> {PHONE_DISPLAY}
              </a>
            </li>
            <li>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp-link" onClick={() => track("whatsapp_click")} className="inline-flex items-center gap-2.5 hover:text-gold-dark">
                <MessageCircle size={15} className="text-gold-dark" /> WhatsApp Us
              </a>
            </li>
            <li>
              <a href={`mailto:${EMAIL}`} data-testid="footer-email-link" onClick={() => track("email_click")} className="inline-flex items-center gap-2.5 hover:text-gold-dark">
                <Mail size={15} className="text-gold-dark" /> {EMAIL}
              </a>
            </li>
            <li className="flex items-start gap-2.5 text-mist">
              <MapPin size={15} className="mt-0.5 shrink-0 text-gold-dark" /> {ADDRESS}
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-line">
        <div className="container-x flex flex-col items-center justify-between gap-2 py-5 text-xs text-mist sm:flex-row">
          <p>© 2026 Orvella Overseas Private Limited. All Rights Reserved.</p>
          <div className="flex gap-5">
            <Link to="/privacy-policy" className="hover:text-gold-dark">Privacy Policy</Link>
            <Link to="/terms-conditions" className="hover:text-gold-dark">Terms &amp; Conditions</Link>
            <Link to="/cookie-policy" className="hover:text-gold-dark">Cookie Policy</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
